/** @format */

/**
 * Stripe Connect Supported Countries Configuration (Frontend)
 *
 * This file mirrors the server-side configuration for use in the frontend.
 * It provides country information for validation and display purposes.
 */

export interface CountryConfig {
	code: string;
	name: string;
	currency: string;
	requiresSSN: boolean;
	nationalIdLabel: string;
}

/**
 * Stripe Connect supported countries
 * This is a subset for frontend use - the full list is maintained on the server
 */
export const SUPPORTED_COUNTRIES: CountryConfig[] = [
	// North America
	{
		code: "US",
		name: "United States",
		currency: "usd",
		requiresSSN: true,
		nationalIdLabel: "SSN (Last 4 digits)",
	},
	{
		code: "CA",
		name: "Canada",
		currency: "cad",
		requiresSSN: false,
		nationalIdLabel: "Social Insurance Number (SIN)",
	},

	// Europe
	{
		code: "GB",
		name: "United Kingdom",
		currency: "gbp",
		requiresSSN: false,
		nationalIdLabel: "National Insurance Number",
	},
	{
		code: "DE",
		name: "Germany",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Tax ID",
	},
	{
		code: "FR",
		name: "France",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Tax ID",
	},
	{
		code: "ES",
		name: "Spain",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "NIE/NIF",
	},
	{
		code: "IT",
		name: "Italy",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Codice Fiscale",
	},
	{
		code: "NL",
		name: "Netherlands",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "BSN",
	},
	{
		code: "BE",
		name: "Belgium",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "National Register Number",
	},
	{
		code: "IE",
		name: "Ireland",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "PPS Number",
	},
	{
		code: "PT",
		name: "Portugal",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "NIF",
	},
	{
		code: "AT",
		name: "Austria",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Tax ID",
	},
	{
		code: "CH",
		name: "Switzerland",
		currency: "chf",
		requiresSSN: false,
		nationalIdLabel: "AHV Number",
	},
	{
		code: "SE",
		name: "Sweden",
		currency: "sek",
		requiresSSN: false,
		nationalIdLabel: "Personal Identity Number",
	},
	{
		code: "NO",
		name: "Norway",
		currency: "nok",
		requiresSSN: false,
		nationalIdLabel: "Personal Number",
	},
	{
		code: "DK",
		name: "Denmark",
		currency: "dkk",
		requiresSSN: false,
		nationalIdLabel: "CPR Number",
	},
	{
		code: "FI",
		name: "Finland",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Personal Identity Code",
	},
	{
		code: "PL",
		name: "Poland",
		currency: "pln",
		requiresSSN: false,
		nationalIdLabel: "PESEL",
	},
	{
		code: "CZ",
		name: "Czech Republic",
		currency: "czk",
		requiresSSN: false,
		nationalIdLabel: "Birth Number",
	},
	{
		code: "HU",
		name: "Hungary",
		currency: "huf",
		requiresSSN: false,
		nationalIdLabel: "Personal ID",
	},
	{
		code: "RO",
		name: "Romania",
		currency: "ron",
		requiresSSN: false,
		nationalIdLabel: "CNP",
	},
	{
		code: "BG",
		name: "Bulgaria",
		currency: "bgn",
		requiresSSN: false,
		nationalIdLabel: "EGN",
	},
	{
		code: "HR",
		name: "Croatia",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "OIB",
	},
	{
		code: "SK",
		name: "Slovakia",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Birth Number",
	},
	{
		code: "SI",
		name: "Slovenia",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "EMŠO",
	},
	{
		code: "EE",
		name: "Estonia",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Personal ID Code",
	},
	{
		code: "LV",
		name: "Latvia",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Personal Code",
	},
	{
		code: "LT",
		name: "Lithuania",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "Personal Code",
	},
	{
		code: "CY",
		name: "Cyprus",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "ID Number",
	},
	{
		code: "MT",
		name: "Malta",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "ID Card Number",
	},
	{
		code: "LU",
		name: "Luxembourg",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "GR",
		name: "Greece",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "AFM",
	},
	{
		code: "LI",
		name: "Liechtenstein",
		currency: "chf",
		requiresSSN: false,
		nationalIdLabel: "Personal ID",
	},
	{
		code: "GI",
		name: "Gibraltar",
		currency: "gbp",
		requiresSSN: false,
		nationalIdLabel: "ID Card Number",
	},

	// Asia-Pacific
	{
		code: "AU",
		name: "Australia",
		currency: "aud",
		requiresSSN: false,
		nationalIdLabel: "Tax File Number",
	},
	{
		code: "NZ",
		name: "New Zealand",
		currency: "nzd",
		requiresSSN: false,
		nationalIdLabel: "IRD Number",
	},
	{
		code: "SG",
		name: "Singapore",
		currency: "sgd",
		requiresSSN: false,
		nationalIdLabel: "NRIC/FIN",
	},
	{
		code: "HK",
		name: "Hong Kong",
		currency: "hkd",
		requiresSSN: false,
		nationalIdLabel: "HKID",
	},
	{
		code: "JP",
		name: "Japan",
		currency: "jpy",
		requiresSSN: false,
		nationalIdLabel: "My Number",
	},
	{
		code: "MY",
		name: "Malaysia",
		currency: "myr",
		requiresSSN: false,
		nationalIdLabel: "NRIC",
	},
	{
		code: "TH",
		name: "Thailand",
		currency: "thb",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "ID",
		name: "Indonesia",
		currency: "idr",
		requiresSSN: false,
		nationalIdLabel: "NIK",
	},
	{
		code: "PH",
		name: "Philippines",
		currency: "php",
		requiresSSN: false,
		nationalIdLabel: "TIN",
	},
	{
		code: "VN",
		name: "Vietnam",
		currency: "vnd",
		requiresSSN: false,
		nationalIdLabel: "CCCD",
	},
	{
		code: "IN",
		name: "India",
		currency: "inr",
		requiresSSN: false,
		nationalIdLabel: "Aadhaar/PAN",
	},

	// Latin America
	{
		code: "MX",
		name: "Mexico",
		currency: "mxn",
		requiresSSN: false,
		nationalIdLabel: "CURP/RFC",
	},
	{
		code: "BR",
		name: "Brazil",
		currency: "brl",
		requiresSSN: false,
		nationalIdLabel: "CPF",
	},

	// Middle East & Africa
	{
		code: "AE",
		name: "United Arab Emirates",
		currency: "aed",
		requiresSSN: false,
		nationalIdLabel: "Emirates ID",
	},

	// Africa
	{
		code: "ZA",
		name: "South Africa",
		currency: "zar",
		requiresSSN: false,
		nationalIdLabel: "National ID Number",
	},
	{
		code: "NG",
		name: "Nigeria",
		currency: "ngn",
		requiresSSN: false,
		nationalIdLabel: "BVN",
	},
	{
		code: "GH",
		name: "Ghana",
		currency: "ghs",
		requiresSSN: false,
		nationalIdLabel: "Ghana Card Number",
	},
	{
		code: "KE",
		name: "Kenya",
		currency: "kes",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "CI",
		name: "Ivory Coast",
		currency: "xof",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "SN",
		name: "Senegal",
		currency: "xof",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "EG",
		name: "Egypt",
		currency: "egp",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "MA",
		name: "Morocco",
		currency: "mad",
		requiresSSN: false,
		nationalIdLabel: "CIN",
	},
	{
		code: "TZ",
		name: "Tanzania",
		currency: "tzs",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "RW",
		name: "Rwanda",
		currency: "rwf",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "ET",
		name: "Ethiopia",
		currency: "etb",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},

	// Caribbean
	{
		code: "TT",
		name: "Trinidad and Tobago",
		currency: "ttd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "JM",
		name: "Jamaica",
		currency: "jmd",
		requiresSSN: false,
		nationalIdLabel: "TRN",
	},

	// Additional Latin America
	{
		code: "AR",
		name: "Argentina",
		currency: "ars",
		requiresSSN: false,
		nationalIdLabel: "CUIT/CUIL",
	},
	{
		code: "CL",
		name: "Chile",
		currency: "clp",
		requiresSSN: false,
		nationalIdLabel: "RUT",
	},
	{
		code: "CO",
		name: "Colombia",
		currency: "cop",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "PE",
		name: "Peru",
		currency: "pen",
		requiresSSN: false,
		nationalIdLabel: "DNI/RUC",
	},
	{
		code: "UY",
		name: "Uruguay",
		currency: "uyu",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},

	// Additional Asia-Pacific
	{
		code: "KR",
		name: "South Korea",
		currency: "krw",
		requiresSSN: false,
		nationalIdLabel: "Resident Registration Number",
	},
	{
		code: "BN",
		name: "Brunei",
		currency: "bnd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "KH",
		name: "Cambodia",
		currency: "khr",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},

	// Additional Middle East
	{
		code: "BH",
		name: "Bahrain",
		currency: "bhd",
		requiresSSN: false,
		nationalIdLabel: "CPR Number",
	},
	{
		code: "SA",
		name: "Saudi Arabia",
		currency: "sar",
		requiresSSN: false,
		nationalIdLabel: "National ID / Iqama",
	},
	{
		code: "QA",
		name: "Qatar",
		currency: "qar",
		requiresSSN: false,
		nationalIdLabel: "QID",
	},
	{
		code: "KW",
		name: "Kuwait",
		currency: "kwd",
		requiresSSN: false,
		nationalIdLabel: "Civil ID",
	},
	{
		code: "OM",
		name: "Oman",
		currency: "omr",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "JO",
		name: "Jordan",
		currency: "jod",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},

	// Europe (additional)
	{
		code: "AL",
		name: "Albania",
		currency: "all",
		requiresSSN: false,
		nationalIdLabel: "Personal ID",
	},
	{
		code: "BA",
		name: "Bosnia and Herzegovina",
		currency: "bam",
		requiresSSN: false,
		nationalIdLabel: "JMBG",
	},
	{
		code: "IS",
		name: "Iceland",
		currency: "isk",
		requiresSSN: false,
		nationalIdLabel: "Kennitala",
	},
	{
		code: "MC",
		name: "Monaco",
		currency: "eur",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "MD",
		name: "Moldova",
		currency: "mdl",
		requiresSSN: false,
		nationalIdLabel: "IDNP",
	},
	{
		code: "MK",
		name: "North Macedonia",
		currency: "mkd",
		requiresSSN: false,
		nationalIdLabel: "Personal ID",
	},
	{
		code: "RS",
		name: "Serbia",
		currency: "rsd",
		requiresSSN: false,
		nationalIdLabel: "JMBG",
	},
	{
		code: "AM",
		name: "Armenia",
		currency: "amd",
		requiresSSN: false,
		nationalIdLabel: "Passport / National ID",
	},
	{
		code: "TR",
		name: "Turkey",
		currency: "try",
		requiresSSN: false,
		nationalIdLabel: "TC Kimlik No",
	},
	{
		code: "IL",
		name: "Israel",
		currency: "ils",
		requiresSSN: false,
		nationalIdLabel: "Teudat Zehut",
	},

	// Asia-Pacific (additional)
	{
		code: "MO",
		name: "Macao SAR China",
		currency: "mop",
		requiresSSN: false,
		nationalIdLabel: "BIR",
	},
	{
		code: "TW",
		name: "Taiwan",
		currency: "twd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "MN",
		name: "Mongolia",
		currency: "mnt",
		requiresSSN: false,
		nationalIdLabel: "Registration Number",
	},
	{
		code: "LK",
		name: "Sri Lanka",
		currency: "lkr",
		requiresSSN: false,
		nationalIdLabel: "NIC",
	},
	{
		code: "PK",
		name: "Pakistan",
		currency: "pkr",
		requiresSSN: false,
		nationalIdLabel: "CNIC",
	},
	{
		code: "UZ",
		name: "Uzbekistan",
		currency: "uzs",
		requiresSSN: false,
		nationalIdLabel: "Passport / PINFL",
	},

	// Latin America (additional)
	{
		code: "BO",
		name: "Bolivia",
		currency: "bob",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "CR",
		name: "Costa Rica",
		currency: "crc",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "DO",
		name: "Dominican Republic",
		currency: "dop",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "EC",
		name: "Ecuador",
		currency: "usd",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "SV",
		name: "El Salvador",
		currency: "usd",
		requiresSSN: false,
		nationalIdLabel: "DUI",
	},
	{
		code: "GT",
		name: "Guatemala",
		currency: "gtq",
		requiresSSN: false,
		nationalIdLabel: "DPI",
	},
	{
		code: "GY",
		name: "Guyana",
		currency: "gyd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "PA",
		name: "Panama",
		currency: "usd",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},
	{
		code: "PY",
		name: "Paraguay",
		currency: "pyg",
		requiresSSN: false,
		nationalIdLabel: "Cedula",
	},

	// Caribbean (additional)
	{
		code: "AG",
		name: "Antigua and Barbuda",
		currency: "xcd",
		requiresSSN: false,
		nationalIdLabel: "Passport / National ID",
	},
	{
		code: "BS",
		name: "Bahamas",
		currency: "bsd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "LC",
		name: "St. Lucia",
		currency: "xcd",
		requiresSSN: false,
		nationalIdLabel: "Passport / National ID",
	},

	// Africa (additional)
	{
		code: "BJ",
		name: "Benin",
		currency: "xof",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "BW",
		name: "Botswana",
		currency: "bwp",
		requiresSSN: false,
		nationalIdLabel: "Omang",
	},
	{
		code: "GM",
		name: "Gambia",
		currency: "gmd",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "MG",
		name: "Madagascar",
		currency: "mga",
		requiresSSN: false,
		nationalIdLabel: "CIN",
	},
	{
		code: "MU",
		name: "Mauritius",
		currency: "mur",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "NA",
		name: "Namibia",
		currency: "nad",
		requiresSSN: false,
		nationalIdLabel: "National ID",
	},
	{
		code: "TN",
		name: "Tunisia",
		currency: "tnd",
		requiresSSN: false,
		nationalIdLabel: "CIN",
	},
];

/**
 * Get country configuration by country code
 */
export const getCountryByCode = (code: string): CountryConfig | undefined => {
	return SUPPORTED_COUNTRIES.find(
		(c) => c.code.toUpperCase() === code?.toUpperCase(),
	);
};

/**
 * Check if a country is supported
 */
export const isCountrySupported = (code: string): boolean => {
	return getCountryByCode(code) !== undefined;
};

/**
 * Get all supported country codes
 */
export const getSupportedCountryCodes = (): string[] => {
	return SUPPORTED_COUNTRIES.map((c) => c.code);
};

/**
 * Check if a country requires SSN
 */
export const countryRequiresSSN = (code: string): boolean => {
	const country = getCountryByCode(code);
	return country?.requiresSSN ?? false;
};
